import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { NeroLogo } from '../bilesenler/Logo.js';

/**
 * GİRİŞ GEREKTİRMEYEN SAYFALAR
 *
 * NİÇİN VARLAR
 * Google, bir uygulamanın Drive'a bağlanmasına izin vermeden önce üç şeyi
 * herkese açık bir adreste görmek ister: uygulamanın ne yaptığı, gizlilik
 * politikası ve kullanım koşulları. İnceleyen kişinin Nero Pro hesabı
 * yoktur; bu yüzden bu üç sayfa OTURUM AÇMADAN açılır.
 *
 * NE YOKTUR
 * Bu sayfalar hiçbir API'ye istek atmaz, hiçbir iş verisi göstermez ve
 * hiçbir şey yazmaz. Salt metindir. Uygulamanın geri kalanına yalnızca
 * "Giriş yap" bağlantısıyla açılır.
 *
 * Bu rotalar `Uygulama.tsx` içinde giriş kontrolünün ÖNÜNDE tanımlanır.
 */

/** Bu üç adres oturum kontrolünün dışındadır — tek kaynak burasıdır. */
export const KAMUSAL_YOLLAR = ['/oauth/nero-pro-backup', '/oauth/privacy', '/oauth/terms'] as const;

export function kamusalMi(yol: string): boolean {
  return (KAMUSAL_YOLLAR as readonly string[]).includes(yol.replace(/\/+$/, '') || '/');
}

const GUNCELLEME = '8 Eylül 2026';

function Sayfa({ baslik, alt, children }: { baslik: string; alt: string; children: ReactNode }) {
  return (
    <div className="kamusal">
      <header className="kamusal-ust">
        <NeroLogo yukseklik={26} />
        <span className="kamusal-marka">Nero Pro</span>
      </header>
      <main className="kamusal-govde">
        <h1>{baslik}</h1>
        <p className="kamusal-alt">{alt}</p>
        {children}
        <hr />
        <p className="kamusal-dip">
          Son güncelleme: {GUNCELLEME} · NeroPerformance, Gaziantep, Türkiye ·{' '}
          <a href="mailto:neroperformancegaziantep@gmail.com">neroperformancegaziantep@gmail.com</a>
        </p>
        <nav className="kamusal-baglar">
          <Link to="/oauth/nero-pro-backup">Uygulama</Link>
          <Link to="/oauth/privacy">Gizlilik</Link>
          <Link to="/oauth/terms">Koşullar</Link>
          <Link to="/">Giriş yap</Link>
        </nav>
      </main>
    </div>
  );
}

/* ---------- /oauth/nero-pro-backup ---------- */

export function OauthTanitim() {
  return (
    <Sayfa
      baslik="Nero Pro — Google Drive Yedekleme"
      alt="Nero Pro'nun Google Drive'ı ne için kullandığı, hangi izni istediği ve neye dokunmadığı."
    >
      <h2>Nero Pro nedir?</h2>
      <p>
        Nero Pro, NeroPerformance Gaziantep'in kendi atölyesinde kullandığı işletme yönetim
        uygulamasıdır: iş emirleri, müşteri ve araç kayıtları, kasa, stok, proforma ve
        performans log analizi. Uygulama yalnızca bu işletmenin çalışanları tarafından,
        kullanıcı adı ve şifreyle kullanılır.
      </p>

      <h2>Google Drive'ı ne için kullanıyor?</h2>
      <p>
        Tek bir amaç için: <strong>işletmenin kendi verisinin yedeğini, işletmenin kendi
        Google hesabına yazmak.</strong> Yedek her gün otomatik alınır ve Drive'da
        <strong> “NERO PRO - YEDEKLER”</strong> adlı bir klasöre yüklenir. Sunucu bir gün
        tamamen kaybolsa bile işletme kayıtları bu arşivden geri getirilebilir.
      </p>
      <p>
        Ayrıca büyük dosyalar — Post Studio'da hazırlanan görseller ve analiz edilen ham log
        dosyaları — sunucu diskini şişirmemek için doğrudan bu klasöre arşivlenir.
      </p>

      <h2>İstenen izin</h2>
      <p>
        Yalnızca <code>drive.file</code> izni istenir. Bu izin, uygulamanın{' '}
        <strong>yalnızca kendi oluşturduğu dosya ve klasörleri</strong> görmesine ve
        yönetmesine izin verir.
      </p>
      <ul>
        <li>Drive'ınızdaki diğer dosyalar uygulamaya <strong>görünmez</strong>.</li>
        <li>Var olan dosyalarınız <strong>okunmaz, değiştirilmez, silinmez</strong>.</li>
        <li>Tam <code>drive</code> izni <strong>istenmez</strong>.</li>
      </ul>

      <h2>Nereye ne yazılır</h2>
      <table className="kamusal-tablo">
        <thead><tr><th>Yer</th><th>Ne durur</th></tr></thead>
        <tbody>
          <tr><td>PostgreSQL veritabanı</td><td>Canlı iş verisi — tek doğruluk kaynağı budur.</td></tr>
          <tr><td>Uygulama sunucusu</td><td>Son günlerin acil durum kopyası.</td></tr>
          <tr><td>Google Drive</td><td>Uzun süreli yedek arşivi ve büyük medya dosyaları.</td></tr>
        </tbody>
      </table>
      <p>
        Google Drive <strong>hiçbir zaman canlı veritabanı değildir</strong>; uygulama
        çalışırken oradan veri okumaz.
      </p>

      <h2>Bağlantıyı kaldırma</h2>
      <p>
        Yönetici, uygulamanın Yönetici → Yedekleme ekranından bağlantıyı istediği an
        kesebilir; ayrıca{' '}
        <a href="https://myaccount.google.com/permissions" target="_blank" rel="noreferrer noopener">
          Google Hesap İzinleri
        </a>{' '}
        sayfasından da erişimi iptal edebilir. Her iki durumda da{' '}
        <strong>Drive'daki yedek dosyalarına dokunulmaz</strong>; onlar hesabın sahibinde kalır.
      </p>
    </Sayfa>
  );
}

/* ---------- /oauth/privacy ---------- */

export function Gizlilik() {
  return (
    <Sayfa
      baslik="Gizlilik Politikası"
      alt="Nero Pro'nun hangi veriyi topladığı, nerede tuttuğu ve kimseyle paylaşmadığı."
    >
      <h2>Kısaca</h2>
      <p>
        Nero Pro halka açık bir hizmet değildir. NeroPerformance Gaziantep'in kendi
        kayıtlarını tuttuğu bir iç uygulamadır. Veri satılmaz, reklam için kullanılmaz,
        üçüncü taraflarla paylaşılmaz.
      </p>

      <h2>Toplanan veri</h2>
      <ul>
        <li><strong>İşletme verisi:</strong> müşteri, araç, iş emri, kasa, stok, proforma kayıtları.</li>
        <li><strong>Kullanıcı hesapları:</strong> çalışan adı, kullanıcı adı ve şifrenin geri
          döndürülemez özeti (şifreler düz metin olarak saklanmaz).</li>
        <li><strong>İşlem geçmişi:</strong> kim ne zaman ne yaptı — hesap verebilirlik için.</li>
        <li><strong>Yüklenen dosyalar:</strong> araç fotoğrafları, ECU dosyaları, log dosyaları.</li>
      </ul>
      <p>Uygulama izleme çerezi, analitik veya reklam kimliği kullanmaz.</p>

      <h2>Google kullanıcı verisi</h2>
      <p>
        Google ile bağlantı kurulduğunda saklanan tek şey, uygulamanın kendi yedek
        klasörüne yazabilmesi için gereken <strong>yetkilendirme anahtarıdır</strong>. Bu anahtar:
      </p>
      <ul>
        <li>Veritabanına <strong>şifrelenmiş</strong> olarak yazılır (AES-256-GCM).</li>
        <li>Tarayıcıya <strong>hiç gönderilmez</strong>, kayıt dosyalarına yazılmaz.</li>
        <li>Alınan <strong>yedeklerin içine konmaz</strong>.</li>
        <li>Yalnızca yedek yüklemek için kullanılır; başka hiçbir amaçla kullanılmaz.</li>
      </ul>
      <p>
        Uygulama Google hesabınızdan e-posta, kişi, takvim ya da başka bir veri okumaz.
        Drive'da yalnızca <strong>kendi oluşturduğu</strong> dosyaları görür.
      </p>
      <p>
        Nero Pro'nun Google API'lerinden aldığı bilgilerin kullanımı,{' '}
        <a
          href="https://developers.google.com/terms/api-services-user-data-policy"
          target="_blank" rel="noreferrer noopener"
        >
          Google API Hizmetleri Kullanıcı Verileri Politikası
        </a>{' '}
        ile Sınırlı Kullanım şartlarına uygundur.
      </p>

      <h2>Verinin nerede durduğu</h2>
      <p>
        Canlı veri, yönetilen bir PostgreSQL veritabanında ve uygulama sunucusunda durur.
        Yedekler işletmenin kendi Google Drive hesabındadır. Sunucuya erişim şifreyle
        korunur ve tüm trafik HTTPS üzerinden geçer.
      </p>

      <h2>Saklama ve silme</h2>
      <p>
        Sunucudaki yedekler kısa bir pencerede (varsayılan 14 gün) tutulur ve otomatik
        temizlenir. <strong>Google Drive'daki arşiv otomatik silinmez</strong>; oradaki
        dosyaların akıbetine yalnızca hesabın sahibi karar verir. Bağlantı kesildiğinde
        saklanan yetkilendirme anahtarı silinir.
      </p>

      <h2>İletişim</h2>
      <p>
        Sorular için:{' '}
        <a href="mailto:neroperformancegaziantep@gmail.com">neroperformancegaziantep@gmail.com</a>
      </p>
    </Sayfa>
  );
}

/* ---------- /oauth/terms ---------- */

export function Kosullar() {
  return (
    <Sayfa
      baslik="Kullanım Koşulları"
      alt="Nero Pro'nun kimin için olduğu ve nasıl kullanıldığı."
    >
      <h2>Kapsam</h2>
      <p>
        Nero Pro, NeroPerformance Gaziantep'in iç kullanımına yönelik bir işletme yönetim
        uygulamasıdır. Genel kullanıma açık değildir; hesaplar yalnızca işletme yöneticisi
        tarafından açılır.
      </p>

      <h2>Hesaplar</h2>
      <ul>
        <li>Her çalışanın kendi hesabı vardır; hesap paylaşılmaz.</li>
        <li>Yetkiler role göre verilir; kimse yetkisi olmayan veriyi göremez.</li>
        <li>Yapılan işlemler kullanıcı adıyla birlikte kaydedilir.</li>
      </ul>

      <h2>Kabul edilebilir kullanım</h2>
      <p>
        Uygulama yalnızca işletmenin kendi işleri için kullanılır. Sisteme izinsiz erişmeye
        çalışmak, başkasının hesabını kullanmak veya işletme verisini yetkisiz kişilerle
        paylaşmak yasaktır.
      </p>

      <h2>Yedekleme</h2>
      <p>
        Yedekleme, veri kaybı riskini azaltır ama sıfırlamaz. İşletme, yedeklerin
        alındığını Yönetici → Yedekleme ekranından düzenli olarak kontrol etmekle
        yükümlüdür. Google Drive'a yüklenen yedekler işletmenin kendi Google hesabında
        durur ve o hesabın kurallarına tabidir.
      </p>

      <h2>Sorumluluk</h2>
      <p>
        Uygulama “olduğu gibi” sunulur. Veri girişinin doğruluğu kullanıcıya aittir.
        Yasal ve mali kayıtların resmî muhasebe kayıtlarıyla uyumundan işletme sorumludur.
      </p>

      <h2>Değişiklikler</h2>
      <p>
        Bu koşullar güncellenebilir. Güncel sürüm her zaman bu adreste yayımlanır ve
        yukarıdaki tarih değişiklik gününü gösterir.
      </p>
    </Sayfa>
  );
}
